import { API_BASE_URL } from '@synergy/config';
import { BankIcon, EuroIcon } from '@synergy/theme';
import { Box, TextField, Theme, Tooltip } from '@mui/material';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormComponent } from '@synergy/abstract';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { FunctionalAsset, NotificationOrigin, PaymentMethod } from '@synergy/models';
import { PaloField } from '@synergy/components-palo';
import { ReactElement } from 'react';
import { WithTheme as WithThemeProps, withTheme } from '@mui/styles';
import { connect } from 'react-redux';
import { createRef } from 'react';

import { Account } from '../../../../../common/models/account';
import { AccountApi } from '../../../../../common/api/account-api';
import { AppState } from '../../../../../store/models/app-state';
import {
    FunctionalAssetAutocomplete,
    FunctionalAssetAutocompleteRef,
} from '../../../../../common/components/functional-asset-autocomplete/functional-asset-autocomplete';
import { PaloNotificationOverrideHint } from '../../../../../common/components/palo/palo-notification-override-hint';
import { PaloOrderSupplierLock } from '../../../../../common/components/palo/palo-locked-badge';
import { PaloSupplierBonusBadge } from '../../../../../common/components/palo/palo-supplier-bonus-badge';
import { PaloSupplierChangeDialog } from '../../../../../common/components/palo/palo-supplier-change-dialog';
import {
    PartialAccountContact,
    PoolAccountContactAutocompleteList,
} from '../../../../../common/components/autocomplete-list/account-contact-autocomplete-list/pool-account-contact-autocomplete-list';
import {
    PartialSupplier,
    PoolSupplierAutocompleteList,
} from '../../../../../common/components/autocomplete-list/supplier-autocomplete-list/pool-supplier-autocomplete-list';
import {
    PaymentTermAutocomplete,
    PaymentTermAutocompleteRef,
} from '../../../../../common/components/payment-term-autocomplete/payment-term-autocomplete';
import {
    PurchaseOrderDetailOverviewFormValues,
    purchaseOrderDetailOverviewFieldNames as fieldNames,
} from '../models/purchase-order-detail-form-values';
import { Supplier } from '../../../../../common/models/supplier';
import { TermsAndConditions } from '../../../../../common/models/terms-and-conditions';
import { clearSupplierConditions, fetchSupplierConditions } from '../../../../../common/actions/supplier-actions';
import { usePurchaseOrderDetailContext } from '../../context/purchase-order-context';

interface PaloOverviewSectionSupplierReduxProps {
    termsAndConditions: TermsAndConditions | undefined;
    poolAccount: Account;
    clearSupplierConditions: () => void;
    fetchSupplierConditions: (supplierId: number) => void;
}

interface PaloOverviewSectionSupplierOwnProps extends WithThemeProps<Theme> {
    /** How many positions the order carries, and how to empty it. */
    positionCount?: number;
    removeAllPositions?: () => void;
    readonly?: boolean;
    formikBag: FormikProps<PurchaseOrderDetailOverviewFormValues>;
}

type PaloOverviewSectionSupplierProps = PaloOverviewSectionSupplierOwnProps &
    PaloOverviewSectionSupplierReduxProps &
    WrappedComponentProps;

interface PaloOverviewSectionSupplierState {
    pending?: { supplier: Supplier | null };
}

class PaloOverviewSectionSupplierBase extends FormComponent<
    PurchaseOrderDetailOverviewFormValues,
    PaloOverviewSectionSupplierProps,
    PaloOverviewSectionSupplierState
> {
    private paymentTermsRef = createRef<PaymentTermAutocompleteRef>();
    private functionalAssetsRef = createRef<FunctionalAssetAutocompleteRef>();

    /**
     * A supplier picked while positions are already on the order waits here
     * until it is clear what happens to them (Prüfbericht 28.09.2026).
     */
    public state: PaloOverviewSectionSupplierState = {};

    /**
     * The name of the supplier the order had. Clearing the field empties the
     * value before the change is reported, so the question that follows could
     * no longer say whom the positions came from.
     */
    private lastSupplierName?: string;

    /** The supplier whose contacts are currently being read. */
    private lastContactSupplierId?: number;

    private api = new AccountApi(API_BASE_URL);

    private handleSupplierSelected =
        (formikBag: FormikProps<PurchaseOrderDetailOverviewFormValues>) => async (supplier: Supplier | null) => {
            const current = formikBag.values.supplierAccount?.id;

            // The first supplier, nothing picked yet, or the same one again:
            // no question — nothing could be left orphaned.
            if (!current || !this.props.positionCount || current === supplier?.id) {
                await this.applySupplier(formikBag)(supplier);

                return;
            }

            this.setState({ pending: { supplier } });
        };

    /**
     * Puts the supplier's main contact into the contact field.
     *
     * Picking a supplier left it empty, so every order had to have the person
     * looked up by hand although the master record names one — and the order
     * cannot be sent without a contact (#2239 R-138). Whoever the user picks
     * afterwards stands; this only fills what was empty.
     */
    private fillContact = async (
        formikBag: FormikProps<PurchaseOrderDetailOverviewFormValues>,
        supplier: Supplier | null
    ): Promise<void> => {
        this.lastContactSupplierId = supplier?.id;

        if (!supplier?.id) {
            return;
        }

        const contacts = await this.api.getContacts(supplier.id);

        // The supplier may have been changed again while this was on the way.
        if (!contacts.success || this.lastContactSupplierId !== supplier.id) {
            return;
        }

        const contact =
            contacts.result.find(candidate => candidate.isMainContact) ??
            (1 === contacts.result.length ? contacts.result[0] : undefined);

        if (contact) {
            formikBag.setFieldValue(fieldNames.supplierContact, contact);
        }
    };

    private applySupplier =
        (formikBag: FormikProps<PurchaseOrderDetailOverviewFormValues>) => async (supplier: Supplier | null) => {
            const oldSupplier = formikBag.values.supplierAccount;
            const oldSupplierId = oldSupplier && oldSupplier.id;

            if (supplier?.isChargingSupplier) {
                formikBag.setFieldValue(fieldNames.chargingInvoiceAddress, this.props.poolAccount.mainAddress);
            } else {
                formikBag.setFieldValue(fieldNames.chargingInvoiceAddress, formikBag.values.invoiceAddress);
            }

            if (supplier === null || oldSupplierId !== supplier.id) {
                formikBag.setFieldValue(fieldNames.supplierAccount, supplier);
                formikBag.setFieldValue(fieldNames.supplierContact, null);
                void this.fillContact(formikBag, supplier);
                formikBag.setFieldValue(fieldNames.supplierBonus, '');
                formikBag.setFieldValue(fieldNames.functionalAsset, null);
                formikBag.setFieldValue(fieldNames.requireFunctionalAssets, supplier?.requireFunctionalAssets);

                const paymentTerm = this.paymentTermsRef.current?.getPaymentTerm(supplier?.paymentTermId);
                formikBag.setFieldValue(fieldNames.paymentTerm, paymentTerm);

                if (supplier) {
                    this.props.fetchSupplierConditions(supplier.id);
                } else {
                    this.props.clearSupplierConditions();
                }
            }
        };

    private getCurrentSupplierAccountId(formikBag: FormikProps<PurchaseOrderDetailOverviewFormValues>) {
        return formikBag.values.supplierAccount?.id;
    }

    public componentWillUnmount() {
        this.props.clearSupplierConditions();
    }

    public UNSAFE_componentWillReceiveProps(nextProps: PaloOverviewSectionSupplierProps) {
        if (nextProps.termsAndConditions !== this.props.termsAndConditions) {
            this.props.formikBag.setFieldValue(
                fieldNames.supplierBonus,
                nextProps.termsAndConditions?.bonusConditions ?? ''
            );
            this.props.formikBag.setFieldValue(
                fieldNames.termsOfDeliveryContent,
                nextProps.termsAndConditions?.termsOfDelivery ?? ''
            );
            this.props.formikBag.setFieldValue(
                fieldNames.paymentTerm,
                nextProps.termsAndConditions?.paymentTerm ?? null
            );
        }
    }

    public render() {
        const { readonly, formikBag } = this.props;

        const supplier = this.props.formikBag.values.supplierAccount;

        this.lastSupplierName = supplier?.name ?? this.lastSupplierName;

        const adornment = [
            supplier?.isChargingSupplier ? (
                <Tooltip
                    key={1}
                    title={<FormattedMessage id="app.purchaseOrders.customerAddressOverride" />}
                    placement="top-end"
                >
                    <Box sx={{ height: '20px', mr: 1 }}>
                        <EuroIcon sx={{ fontSize: 20 }} htmlColor={this.props.theme.palette.secondary.main} />
                    </Box>
                </Tooltip>
            ) : null,
            supplier?.paymentMethod === PaymentMethod.Debit ? (
                <Tooltip key={2} title={<FormattedMessage id="app.purchaseOrders.supplierDebit" />} placement="top-end">
                    <Box sx={{ height: '20px', mr: 1 }}>
                        <BankIcon sx={{ fontSize: 20 }} htmlColor={this.props.theme.palette.secondary.main} />
                    </Box>
                </Tooltip>
            ) : null,
            <PaloNotificationOverrideHint
                key={3}
                accountId={supplier?.id}
                origins={[NotificationOrigin.PurchaseOrderManual, NotificationOrigin.PurchaseOrderShop]}
            />,
        ];

        return (
            <>
                <PaloSupplierChangeDialog
                    open={undefined !== this.state.pending}
                    count={this.props.positionCount ?? 0}
                    previousSupplier={this.props.formikBag.values.supplierAccount?.name}
                    onCancel={() => this.setState({ pending: undefined })}
                    onKeep={async () => {
                        const picked = this.state.pending;

                        this.setState({ pending: undefined });
                        await this.applySupplier(this.props.formikBag)(picked?.supplier ?? null);
                    }}
                    onRemove={async () => {
                        const picked = this.state.pending;

                        this.setState({ pending: undefined });
                        this.props.removeAllPositions?.();
                        await this.applySupplier(this.props.formikBag)(picked?.supplier ?? null);
                    }}
                />
                <Field name={fieldNames.supplierAccount}>
                    {({ field, form }: FieldProps<PartialSupplier>) => (
                        <PaloField
                            label={
                                <>
                                    <FormattedMessage id="app.label.supplierAccount" /> *
                                </>
                            }
                            name={field.name}
                            badge={<PaloOrderSupplierLock />}
                            area="supplier"
                            error={this.hasError(form, field.name) ? this.getError(form, field.name) : undefined}
                        >
                            <PoolSupplierAutocompleteList
                                id="purchase-order-autocomplete-supplier"
                                label={<></>}
                                required={false}
                                onSelected={this.handleSupplierSelected(form)}
                                initialValue={''}
                                error={this.hasError(form, field.name)}
                                readOnly={readonly}
                                {...field}
                                adornment={adornment}
                                useSupplierFilterForCustomerId={formikBag.values.customerAccount?.id}
                                isLocked={false}
                            />
                        </PaloField>
                    )}
                </Field>
                <Field name={fieldNames.supplierContact}>
                    {({ field, form }: FieldProps<PartialAccountContact>) => (
                        <PaloField
                            label={<FormattedMessage id="app.label.supplierContactFull" />}
                            name={field.name}
                            area="supplierContact"
                            error={this.hasError(form, field.name) ? this.getError(form, field.name) : undefined}
                        >
                            <PoolAccountContactAutocompleteList
                                accountId={this.getCurrentSupplierAccountId(form)}
                                accountType="supplier"
                                label={<></>}
                                onSelected={this.setFieldValue(form, field.name)}
                                error={this.hasError(form, field.name)}
                                disabled={!form.values.supplierAccount}
                                readOnly={readonly}
                                {...field}
                            />
                        </PaloField>
                    )}
                </Field>
                <Field name={fieldNames.functionalAsset}>
                    {({ form }: FieldProps<FunctionalAsset>) => {
                        const required =
                            (form.values?.supplierAccount?.requireFunctionalAssets ||
                                form.values?.requireFunctionalAssets) ??
                            false;

                        return (
                            <PaloField
                                label={
                                    <>
                                        <FormattedMessage id="app.label.functionalAsset" />
                                        {required ? ' *' : ''}
                                    </>
                                }
                                area="functionalAsset"
                            >
                                <FunctionalAssetAutocomplete
                                    name={fieldNames.functionalAsset}
                                    label={<></>}
                                    supplierId={form.values.supplierAccount?.id}
                                    customerId={form.values.customerAccount?.id}
                                    ref={this.functionalAssetsRef}
                                    required={false}
                                    disabled={
                                        (!form.values.requireFunctionalAssets &&
                                            !form.values.supplierAccount?.requireFunctionalAssets) ||
                                        readonly
                                    }
                                />
                            </PaloField>
                        );
                    }}
                </Field>
                <Field name={fieldNames.paymentTerm}>
                    {() => (
                        <PaloField
                            label={<FormattedMessage id="app.contactsAndOrganizations.detail.paymentTerm" />}
                            area="paymentTerm"
                        >
                            <PaymentTermAutocomplete
                                name={fieldNames.paymentTerm}
                                ref={this.paymentTermsRef}
                                label={<></>}
                                disabled={readonly}
                            />
                        </PaloField>
                    )}
                </Field>
                <Field name={fieldNames.termsOfDeliveryContent}>
                    {({ field, form }: FieldProps<string>) => (
                        <PaloField
                            label={<FormattedMessage id="app.label.termsOfDelivery" />}
                            name={field.name}
                            area="termsOfDelivery"
                            error={this.hasError(form, field.name) ? this.getError(form, field.name) : undefined}
                        >
                            <TextField
                                {...field}
                                value={field.value ?? ''}
                                size="small"
                                fullWidth
                                multiline
                                minRows={1}
                                maxRows={6}
                                disabled={readonly}
                                error={this.hasError(form, field.name)}
                            />
                        </PaloField>
                    )}
                </Field>
                {/* The text stays the order's copy of the conditions and is saved
                    with it; what stands here is the badge every document shows
                    (#2259, GF 14.10.2026). */}
                <PaloField label={<FormattedMessage id="app.label.supplierBonus" />} area="supplierBonus">
                    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 26 }}>
                        <PaloSupplierBonusBadge
                            supplierId={formikBag.values.supplierAccount?.id}
                            supplierName={formikBag.values.supplierAccount?.name}
                            at={formikBag.values.orderDate}
                        />
                    </Box>
                </PaloField>
            </>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    termsAndConditions: state.suppliers.termsAndConditions,
    poolAccount: state.poolAccount,
});

const ConnectedPaloOverviewSectionSupplier = connect(mapStateToProps, {
    fetchSupplierConditions,
    clearSupplierConditions,
})(PaloOverviewSectionSupplierBase);

const IntlPaloOverviewSectionSupplier = injectIntl(ConnectedPaloOverviewSectionSupplier);
const ThemedPaloOverviewSectionSupplier = withTheme<Theme, typeof IntlPaloOverviewSectionSupplier>(
    IntlPaloOverviewSectionSupplier
);

/** Hands the section what the positions of the order say about themselves. */
export const PaloOverviewSectionSupplier = (
    props: Omit<PaloOverviewSectionSupplierOwnProps, 'theme' | 'positionCount' | 'removeAllPositions'>
): ReactElement => {
    const { positionCount, removeAllPositions } = usePurchaseOrderDetailContext();

    return (
        <ThemedPaloOverviewSectionSupplier
            {...props}
            positionCount={positionCount}
            removeAllPositions={removeAllPositions}
        />
    );
};
